import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchMembershipRequestDetails,
  verifyMembershipRequest,
  approveMembershipRequest,
  queryMembershipRequest,
  rejectMembershipRequest,
  fetchMembershipPendingApprovals,
} from "../../contents/redux/actions/membershipRequestActions";
import { fetchWorkflowRouteActors } from "../../contents/redux/actions/workflowRouteActions";
import styles from "../utilities/cssmodules/ViewMembershipRequest.module.css";
import {
  CalendarMonthRounded,
  DoNotDisturbAltRounded,
  HelpOutlineRounded,
  PersonRounded,
  TaskAltRounded,
  BusinessRounded,
  AccountBalanceRounded,
  DescriptionRounded,
  FilePresentRounded,
  LocalPhoneRounded,
  PhoneAndroidRounded,
  MarkEmailReadRounded,
  PublicRounded,
  PercentRounded,
  BusinessCenterRounded,
  ContactsRounded,
  CurrencyExchangeRounded,
  VerifiedRounded,
  WorkRounded,
  BadgeRounded,
  HomeRounded,
  SupervisedUserCircleRounded,
  AssuredWorkloadRounded,
  GroupsRounded,
  AccountCircleRounded,
  CompareArrowsRounded,
  Inventory2Rounded,
  NotesRounded,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import QueryMembershipRequestModal from "./QueryMembershipRequestModal";
import RejectMembershipRequestModal from "./RejectMembershipRequestModal";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material"; // MUI spinner

import { createSelector } from "reselect";

// Memoized selector for rolePermissions
const selectAuthState = (state) => state.auth;
const selectRolePermissions = createSelector(
  [selectAuthState],
  (auth) => auth?.loggedInUserRolePermissions || [],
);

// Icon mapping for headings
const iconMapping = {
  "Name of Business / Company": BusinessRounded,
  "Date of Establishment": CalendarMonthRounded,
  "Current Office Address": BusinessRounded,
  "Office Contact Number": LocalPhoneRounded,
  "Mobile Number": PhoneAndroidRounded,
  "Email Address": MarkEmailReadRounded,
  "Website or Social Media": PublicRounded,
  "Business Sector": BusinessRounded,
  "Products or Services": BusinessCenterRounded,
  "Tax Identification Number": DescriptionRounded,
  "Actual Business Operation Start Date" : CalendarMonthRounded,
  "Major Shareholders Local Percentage": PercentRounded,
  "Major Shareholders Foreign Percentage": PercentRounded,
  "Business Ownership": ContactsRounded,
  "Annual Turnover (Past 3 years only)": CurrencyExchangeRounded,
  "Number Of Employees": GroupsRounded,
  "Bank Reference": AccountBalanceRounded,
  "Full Name": PersonRounded,
  "Position": WorkRounded,
  "Email": MarkEmailReadRounded,
  "Office Number": LocalPhoneRounded,
  "Address": HomeRounded,
  "Accredited Representative?": BadgeRounded,
  "Expected Benefits": HelpOutlineRounded,
  "Member Of Other Business Organization?": SupervisedUserCircleRounded,
  "Other Business Organization": BusinessRounded,
  "Reference A": VerifiedRounded,
  "Reference B": VerifiedRounded,
  "Overseas Relationships": BusinessRounded,
  "Legal Form": AssuredWorkloadRounded,
  "Documents": FilePresentRounded,
};

const ViewMembershipRequestContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { membershipRequestDetails, isLoading, fetchMembershipRequestDetailsError } = useSelector(
  (state) => state.membershipRequests
);

  // const rolePermissions = useSelector(selectRolePermissions);

  const {
    id: stateId,
    requester,
    requesterId,
  } = location.state || {};

  const requestId = stateId || membershipRequestDetails?.id;
  const loggedInUserRoleId = parseInt(localStorage.getItem("loggedInUserRoleId"), 10);

  const [userPermissions, setUserPermissions] = useState([]);
  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
  const [isRejectCommentModalOpen, setIsRejectCommentModalOpen] = useState(false);
  const [workflowId, setWorkflowId] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0001-01-01T00:00:00") return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  useEffect(() => {
    if (requestId) {
      dispatch(fetchMembershipRequestDetails(requestId));
    } else {
      toast.error("Request ID is missing. Please try again.");
    }
  }, [requestId, dispatch]);

  useEffect(() => {
    if (membershipRequestDetails) {
      const id = membershipRequestDetails?.workflowPermissions?.[0]?.workflowId;
      setWorkflowId(id);

      const permissions =
        membershipRequestDetails?.workflowPermissions
          ?.filter(
            (perm) =>
              perm.roleId === loggedInUserRoleId &&
              ["Verify", "Approve", "Query", "Reject"].includes(perm.permissionName)
          )
          ?.map((perm) => perm.permissionName) || [];
      setUserPermissions(permissions);
    }
  }, [membershipRequestDetails, loggedInUserRoleId]);

  useEffect(() => {
    if (workflowId) {
      dispatch(fetchWorkflowRouteActors(workflowId));
    }
  }, [workflowId, dispatch]);

  // Step fields
  const contactInfoFields = [
    { label: "Name of Business / Company", key: "businessName" },
    { label: "Date of Establishment", key: "dateOfEstablishment", format: formatDate },
    { label: "Current Office Address", key: "officeAddress" },
    { label: "Office Contact Number", key: "officeNumber" },
    { label: "Mobile Number", key: "mobileNumber" },
    { label: "Email Address", key: "email" },
    { label: "Website or Social Media", key: "websiteOrSocial" },
  ];

  const businessInfoFields = [
    { 
      label: "Business Sector", 
      key: "sector" 
    },
    { 
      label: "Products or Services", 
      key: "productsOrServices" 
    },
    { 
      label: "Tax Identification Number", 
      key: "tin" 
    },
    { 
      label: "Actual Business Operation Start Date", 
      key: "operationStartDate" 
    },
    { 
      label: "Major Shareholders Local Percentage", 
      key: "shareholdersLocalPercent" 
    },
    { 
      label: "Major Shareholders Foreign Percentage", 
      key: "shareholdersForeignPercent" 
    },
    { 
      label: "Business Ownership", 
      key: "ownership" 
    },
    { 
      label: "Annual Turnover (Past 3 years only)", 
      key: "annualTurnover" 
    },
    { 
      label: "Annual Turnover Currency", 
      key: "annualTurnoverCurrency" 
    },
    { 
      label: "Number Of Employees", 
      key: "numberOfEmployees" 
    },
    { 
      label: "Bank Reference", 
      key: "bankReference" },
  ];

  const topManagementFields = [
    { label: "Full Name", key: "fullName" },
    { label: "Position", key: "position" },
    { label: "Email", key: "email" },
    { label: "Mobile Number", key: "mobileNumber" },
    { label: "Office Number", key: "officeNumber" },
    { label: "Address", key: "address" },
    { label: "Accredited Representative?", key: "isAccreditedRepresentative", format: (val) => (val ? "Yes" : "No") },
  ];

  const expectationsFields = [
    { label: "Expected Benefits", key: "expectedBenefits" },
    // { label: "Member of Other Business Organization?",  key: "otherOrganizations" },
    { label: "Other Business Organization", key: "otherAssociations" },
  ];

  const referencesFields = [
    { label: "Reference A", key: "referenceA" },
    { label: "Reference A Phone Contact Number", key: "referenceAPhoneNumber" },
    { label: "Reference A Email address", key: "referenceAEmail" },
    { label: "Reference B", key: "referenceB" },
    { label: "Reference B Phone Contact Number", key: "referenceBPhoneNumber" },
    { label: "Reference B Email", key: "referenceBEmail" },
  ];

  // Stepper sections
  const steps = [

    // Contact information
    {
      label: "Contact Information",
      icon: ContactsRounded,
      content: (
        <>
          {membershipRequestDetails?.logo && (
            <div className={styles["member_logo_container"]}>
              <div className={styles["member_logo_heading"]}>Business / Company Logo</div>
              <img
              src={`data:image/png;base64,${membershipRequestDetails.logo}`}
              alt="Member Logo"
              className={styles["member_logo"]}
            />
            </div>
          )}


          {contactInfoFields.map((field) => {
            const FieldIcon = iconMapping[field.label] || PersonRounded;
            return (
              <div className={styles["generic_request_content"]} key={field.key}>
                <div className={styles["generic_request_content_heading"]}>
                  <FieldIcon /> {field.label}:
                </div>
                <div className={styles["generic_request_content_data"]}>
                  {field.format
                    ? field.format(membershipRequestDetails?.[field.key] || location.state?.[field.key])
                    : membershipRequestDetails?.[field.key] || location.state?.[field.key] || "N/A"}
                </div>
              </div>
            );
          })}
        </>
      ),
    },

    // Business information
    {
      label: "Business Information",
      icon: BusinessRounded,
      content: (
        <>
          {businessInfoFields.map((field) => {
            const FieldIcon = iconMapping[field.label] || PersonRounded;
            return (
              <div className={styles["generic_request_content"]} key={field.key}>
                <div className={styles["generic_request_content_heading"]}>
                  <FieldIcon /> {field.label}:
                </div>
                <div className={styles["generic_request_content_data"]}>
                  {field.format
                    ? field.format(membershipRequestDetails?.[field.key] || location.state?.[field.key])
                    : membershipRequestDetails?.[field.key] || location.state?.[field.key] || "0"}
                </div>
              </div>
            );
          })}
        </>
      ),
    },

    // Top management officers
    {
      label: "Top Management Officers",
      icon: SupervisedUserCircleRounded,
      content: (
        <div className={styles["top-management-section"]}>
          {membershipRequestDetails?.officers?.length > 0 ? (
            membershipRequestDetails.officers.map((officer) => (
              <div key={officer.id} className={styles["officer-card"]}>
                <div className={styles["officer-heading"]}>
                  <AccountCircleRounded /> {officer.fullName} ({officer.position})
                </div>
                <div className={styles["officer-details"]}>
                  {topManagementFields.map((field) => {
                    const FieldIcon = iconMapping[field.label] || PersonRounded;
                    return (
                      <div key={field.key} className={styles["officer-details-inner"]}>
                        <div className={styles["officer-details-inner-heading"]}>
                          <FieldIcon /> {field.label}:
                        </div>{" "}
                        <div className={styles["officer-details-inner-data"]}>
                          {field.format ? field.format(officer[field.key]) : officer[field.key] || "Not provided"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div>No officers listed.</div>
          )}
        </div>
      ),
    },

    // Expected benefits
    {
      label: "Expectations of New Members",
      icon: HelpOutlineRounded,
      content: (
        <>
          {expectationsFields.map((field) => {
            const FieldIcon = iconMapping[field.label] || PersonRounded;
            const value = membershipRequestDetails?.[field.key] || location.state?.[field.key] || "None provided";
            if (field.key === "expectedBenefits") {
              return (
                <div className={styles["generic_request_content"]} key={field.key}>
                  <div className={styles["generic_request_content_heading"]}>
                    <FieldIcon /> {field.label}:
                  </div>
                  <textarea className={styles["generic_request_textarea"]} value={value} readOnly />
                </div>
              );
            }
            return (
              <div className={styles["generic_request_content"]} key={field.key}>
                <div className={styles["generic_request_content_heading"]}>
                  <FieldIcon /> {field.label}:
                </div>
                <div className={styles["generic_request_content_data"]}>{value}</div>
              </div>
            );
          })}
        </>
      ),
    },

    // References
    {
      label: "References",
      icon: VerifiedRounded,
      content: (
        <>
          {/* Reference A Card */}
          <div className={styles["officer-card"]}>
            <div className={styles["officer-heading"]}>
              <VerifiedRounded /> Reference A
            </div>
            <div className={styles["officer-details"]}>
              <div className={styles["officer-details-inner"]}>
                <div className={styles["officer-details-inner-heading"]}><NotesRounded/> Name:</div>
                <div className={styles["officer-details-inner-data"]}>
                  {membershipRequestDetails?.referenceA || location.state?.referenceA || "Not provided"}
                </div>
              </div>
              <div className={styles["officer-details-inner"]}>
                <div className={styles["officer-details-inner-heading"]}><LocalPhoneRounded /> Phone Number:</div>
                <div className={styles["officer-details-inner-data"]}>
                  {membershipRequestDetails?.referenceAPhoneNumber || location.state?.referenceAPhoneNumber || "Not provided"}
                </div>
              </div>
              <div className={styles["officer-details-inner"]}>
                <div className={styles["officer-details-inner-heading"]}><MarkEmailReadRounded /> Email:</div>
                <div className={styles["officer-details-inner-data"]}>
                  {membershipRequestDetails?.referenceAEmail || location.state?.referenceAEmail || "Not provided"}
                </div>
              </div>
            </div>
          </div>

          {/* Reference B Card */}
          <div className={styles["officer-card"]}>
            <div className={styles["officer-heading"]}>
              <VerifiedRounded /> Reference B
            </div>
            <div className={styles["officer-details"]}>
              <div className={styles["officer-details-inner"]}>
                <div className={styles["officer-details-inner-heading"]}><NotesRounded/> Name:</div>
                <div className={styles["officer-details-inner-data"]}>
                  {membershipRequestDetails?.referenceB || location.state?.referenceB || "Not provided"}
                </div>
              </div>
              <div className={styles["officer-details-inner"]}>
                <div className={styles["officer-details-inner-heading"]}><LocalPhoneRounded /> Phone Number:</div>
                <div className={styles["officer-details-inner-data"]}>
                  {membershipRequestDetails?.referenceBPhoneNumber || location.state?.referenceBPhoneNumber || "Not provided"}
                </div>
              </div>
              <div className={styles["officer-details-inner"]}>
                <div className={styles["officer-details-inner-heading"]}><MarkEmailReadRounded /> Email:</div>
                <div className={styles["officer-details-inner-data"]}>
                  {membershipRequestDetails?.referenceBEmail || location.state?.referenceBEmail || "Not provided"}
                </div>
              </div>
            </div>
          </div>
        </>
      ),
    },


    // Overseas relationships
    {
      label: "Overseas Relationships",
      icon: PublicRounded,
      content: (
        <>
          {membershipRequestDetails?.relationships?.length > 0 ? (
            membershipRequestDetails.relationships.map((rel) => {
              const activeRelations = [];
              if (rel.businessAbroad) activeRelations.push("Business Abroad");
              if (rel.jointVenture) activeRelations.push("Joint-Venture");
              if (rel.overseasBranch) activeRelations.push("Overseas Branch");
              if (rel.import) activeRelations.push("Import");
              if (rel.export) activeRelations.push("Export");

              return (
                <div key={rel.id} className={styles["officer-card"]}>
                  <div className={styles["officer-heading"]}>
                    <BusinessRounded /> Overseas Relationship
                  </div>
                  <div className={styles["officer-details"]}>
                    <div className={styles["officer-details-inner"]}>
                      <div className={styles["officer-details-inner-heading"]}><CompareArrowsRounded /> Relationship Type:</div> 
                      <div className={styles["officer-details-inner-data"]}>{activeRelations.length > 0 ? activeRelations.join(", ") : "None"}</div>
                    </div>

                    <div className={styles["officer-details-inner"]}>
                      <div className={styles["officer-details-inner-heading"]}><PublicRounded /> Continent:</div> 
                      <div className={styles["officer-details-inner-data"]}>{rel.continent || "N/A"}</div>
                    </div>

                    <div className={styles["officer-details-inner"]}>
                      <div className={styles["officer-details-inner-heading"]}><PublicRounded /> Country:</div> 
                      <div className={styles["officer-details-inner-data"]}>{rel.country || "N/A"}</div>
                    </div>

                    <div className={styles["officer-details-inner"]}>
                      <div className={styles["officer-details-inner-heading"]}><Inventory2Rounded /> Products / Service:</div> 
                      <div className={styles["officer-details-inner-data"]}>{rel.overseasProductsOrServices || "N/A"}</div>
                    </div>

                  </div>
                </div>
              );
            })
          ) : (
            <div>No overseas relationships listed.</div>
          )}
        </>
      ),
    },

    // Legal form
    {
      label: "Legal Form",
      icon: AssuredWorkloadRounded,
      content: (
        <div className={styles["generic_request_content"]}>
          <div className={styles["generic_request_content_heading"]}>
            <AssuredWorkloadRounded /> Legal Form:
          </div>
          <div className={styles["generic_request_content_data"]}>
            {membershipRequestDetails?.legalForm || "N/A"}
          </div>
        </div>
      ),
    },

    // Documents
    {
      label: "Documents",
      icon: FilePresentRounded,
      content: (
        <div className={styles["top-management-section"]}>
          {membershipRequestDetails?.documents?.length > 0 ? (
            membershipRequestDetails.documents.map((doc) => {
              let fileUrl = doc.fileUrl;
              if (!fileUrl && doc.fileContent) {
                const byteCharacters = atob(doc.fileContent);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/pdf" });
                fileUrl = URL.createObjectURL(blob);
              }

              return (
                <div key={doc.id} className={styles["officer-card"]}>
                  <div className={styles["officer-heading"]}>
                    <FilePresentRounded /> {doc.documentType || "Document"}
                  </div>
                  <div className={styles["officer-details"]}>
                    <div className={styles["document-container-inner"]}>
                      <div className={styles["document-container-inner-heading"]}>Original File Name:</div> 
                      <div className={styles["document-container-data"]}>{doc.originalFileName || "Not provided"}</div>
                    </div>

                    <div className={styles["document-container-inner"]}>
                      <div className={styles["document-container-inner-heading"]}>Notes:</div> 
                      <div className={styles["document-container-data"]}>{doc.notes || "Not provided"}</div>
                    </div>

                    <div className={styles["document-container-inner"]}>
                      <div className={styles["document-container-inner-heading"]}>File:</div> 
                      <div className={styles["document-container-data"]}>
                        {fileUrl ? (<a href={fileUrl} target="_blank" rel="noopener noreferrer">{doc.originalFileName || "View File"}</a>) : "Not uploaded"}
                      </div>
                    </div>

                  </div>
                </div>
              );
            })
          ) : (
            <div>No documents uploaded.</div>
          )}
        </div>
      ),
    },

    // Actions bar
    {
      label: "Actions",
      icon: TaskAltRounded,
      content: (
        <div className={styles["generic_request_actions_wrapper"]}>
          <div className={styles["actions-header"]}>
            <div className={styles["section-header"]}>Actions</div>
          </div>
          <div className={styles["generic_request_actions"]}>

            {/* Verification handling */}
            {userPermissions.includes("Verify") && (
              <button
                onClick={async () => {
                  try {
                    await dispatch(
                      verifyMembershipRequest({
                        membershipId: requestId, // membership ID
                        commentText: "Verified", // or get from a modal/textarea
                      })
                    );
                    navigate("/membershipapprovals"); // redirect after verification
                  } catch (error) {
                    toast.error("Failed to verify membership request.");
                  }
                }}
                className={styles["generic_request_button_primary"]}
              >
                <TaskAltRounded /> Verify membership
              </button>
            )}


            {/* Approval handling */}
            {userPermissions.includes("Approve") && (
              <button
                onClick={async () => {
                  try {
                    await dispatch(
                      approveMembershipRequest({
                        membershipId: requestId,
                        commentText: "Approved",
                      })
                    );
                    navigate("/membershipapprovals"); // redirect after approval
                  } catch (error) {
                    toast.error("Failed to approve membership request.");
                  }
                }}
                className={styles["generic_request_button_primary"]}
              >
                <TaskAltRounded /> Approve membership
              </button>
            )}

            {userPermissions.includes("Query") && (
              <button 
                onClick={() => setIsQueryModalOpen(true)} 
                className={styles["generic_request_button_secondary"]}
              >
                <HelpOutlineRounded /> Query membership
              </button>
            )}




            {/* {userPermissions.includes("Reject") && (
              <button onClick={() => setIsRejectCommentModalOpen(true)} 
              className={styles["generic_request_button_danger"]}>
                <DoNotDisturbAltRounded /> Reject
              </button>
            )} */}

            {userPermissions.includes("Reject") && (
              <button 
                onClick={() => setIsRejectCommentModalOpen(true)} 
                className={styles["generic_request_button_danger"]}
              >
                <DoNotDisturbAltRounded /> Reject membership
              </button>
            )}

          </div>
          <div className={styles["stepper-nav-buttons"]}>
            <button className={styles["generic_request_button_secondary"]} onClick={() => setActiveStep((prev) => prev - 1)}>
              Back
            </button>
          </div>
        </div>
      ),
    },
  ];


  if (isLoading) {
  return (
    <div className={styles["spinner-container"]}>
      <CircularProgress />
      <p>Loading membership approval request details...</p>
    </div>
  );
}

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["wrapper"]}>
        <div className={styles["stepper-layout"]}>
          <div className={styles["stepper-sidebar"]}>
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div
                  key={index}
                  className={`${styles["stepper-step"]} ${index === activeStep ? styles["active-step"] : ""}`}
                  onClick={() => setActiveStep(index)}
                >
                  <span className={styles.stepper_step_icon}>{StepIcon && <StepIcon />}</span>
                  <span className={styles.stepper_step_label}>{step.label}</span>
                </div>
              )
            })}
          </div>

          <div className={styles["stepper-content"]}>
            {steps[activeStep].content}
            {activeStep < steps.length - 1 && (
              <div className={styles["stepper-nav-buttons"]}>
                {activeStep > 0 && (
                  <button className={styles["generic_request_button_secondary"]} onClick={() => setActiveStep((prev) => prev - 1)}>
                    Back
                  </button>
                )}
                <button className={styles["generic_request_button_primary"]} onClick={() => setActiveStep((prev) => prev + 1)}>
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Query modal */}
      <QueryMembershipRequestModal
        open={isQueryModalOpen}
        onClose={() => setIsQueryModalOpen(false)}
        onSubmit={(props) =>
          dispatch(queryMembershipRequest({ ...props, membershipId: requestId }))
        }
        workflowId={workflowId}
        requester={requester}
        requesterId={membershipRequestDetails?.requesterId || requesterId}
      />



      {/* Reject modal */}
      <RejectMembershipRequestModal
        open={isRejectCommentModalOpen}
        onClose={() => setIsRejectCommentModalOpen(false)}
        onSubmit={(props) =>
          dispatch(rejectMembershipRequest({ ...props, membershipId: requestId })
        ).then (() => {
          // Reload the table
          dispatch(fetchMembershipPendingApprovals());
        })
        }
        workflowId={workflowId}
        requester={requester}
        requesterId={membershipRequestDetails?.requesterId || requesterId}
      />
    </div>
  );
};

export default ViewMembershipRequestContent;